import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useState } from 'react';

const highlights = [
  { icon: 'ri-shield-check-line', title: '100% 正版授权', desc: '所有推荐平台均持有正规运营资质，内容全部获得正版授权，告别版权风险。' },
  { icon: 'ri-star-line', title: '严格筛选', desc: '每个平台都经过团队实测，从画质、内容量、流畅度等多维度评估后才推荐。' },
  { icon: 'ri-eye-line', title: '客观评测', desc: '坚持独立客观的评测标准，不偏袒任何平台，为用户提供真实可靠的参考信息。' },
  { icon: 'ri-refresh-line', title: '持续更新', desc: '平台信息和推荐列表定期更新，确保用户始终获取最新的免费看片渠道信息。' },
];

const testimonials = [
  {
    name: '张先生',
    role: '电影爱好者',
    content: '通过这个网站找到了好几个之前不知道的正版免费平台，现在看片再也不用到处找盗版资源了，画质好还不用担心病毒。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '追剧达人',
    content: '分类功能特别好用，想看电视剧直接点电视剧分类就行，推荐都很精准，节省了好多时间。',
    rating: 5,
  },
  {
    name: '王同学',
    role: '大学生',
    content: '作为学生党预算有限，这个网站的推荐让我发现原来有这么多免费的正版内容可以看，强烈推荐！',
    rating: 5,
  },
  {
    name: '赵先生',
    role: '纪录片爱好者',
    content: '纪录片分类做得特别棒，央视纪录频道的推荐让我发现了大量高质量纪录片，全部免费，太赞了！',
    rating: 5,
  },
];

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactMessage, setContactMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setContactStatus('success');
      setContactMessage('提交成功！感谢您的留言，我们会尽快回复。');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }
    if (contactForm.message.length > 500) {
      setContactStatus('error');
      setContactMessage('留言内容不能超过500个字符');
      return;
    }
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9son9d8o1vn02ekuj6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setContactMessage('提交成功！感谢您的留言，我们会尽快回复。');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setContactStatus('error');
        setContactMessage(serverMsg.includes('spam') ? '提交失败，请稍后再试' : serverMsg);
      }
    } catch {
      setContactStatus('error');
      setContactMessage('网络错误，请检查您的网络连接后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '免费看片推荐',
        url: 'https://mianfeikanpian.com',
        description: '精选正版免费看片平台，涵盖电影、电视剧、纪录片等海量内容。正版免费看片平台推荐，免费看片不卡顿。',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 免费看片平台推荐',
        description: '了解免费看片推荐团队的品牌故事、口碑评价，以及联系方式。致力于为用户提供最优质的免费看片平台推荐。',
        url: 'https://mianfeikanpian.com/about',
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 免费看片平台推荐 | 品牌介绍与联系方式"
        description="了解免费看片推荐团队的品牌故事、口碑评价和联系方式。致力于为用户提供最优质的免费看片平台推荐，精选正版免费看片渠道。"
        keywords="免费看片,关于我们,免费看片平台推荐,正版免费看片网站,联系我们"
        canonicalUrl="https://mianfeikanpian.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Page Header */}
        <div className="pt-16 bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]} />
            <div className="px-4 md:px-6 pb-8 text-center">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">关于我们</h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                致力于为用户提供最真实、最可靠的免费看片平台推荐
              </p>
            </div>
          </div>
        </div>

        {/* Brand Story */}
        <section className="w-full py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">品牌故事</h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  在网络影视资源日益丰富的今天，找到真正合法、免费且高质量的视频平台却并非易事。盗版网站层出不穷，不仅画质低劣、广告泛滥，更存在个人信息泄露和病毒入侵的安全隐患。
                </p>
                <p>
                  <strong>免费看片推荐</strong>由此诞生。我们是一群热爱影视内容的互联网从业者，深知广大观众对优质免费观影渠道的渴望。我们花费大量时间实测了市面上几乎所有的主流视频平台，精心筛选出那些真正提供正版免费资源的优质渠道。
                </p>
                <p>
                  我们的使命很简单：让每一位观众都能轻松找到最适合自己的免费看片平台，享受高清正版内容带来的极致观影体验，彻底告别盗版和广告的困扰。
                </p>
                <p>
                  本站推荐的每一个平台都经过严格评测，我们关注画质、内容量、播放流畅度、广告频率和用户体验等多个维度，确保推荐内容真实可靠。我们不接受任何平台的付费推广，坚持独立客观的评测标准。
                </p>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20cozy%20workspace%20with%20team%20collaborating%20around%20large%20screen%20displaying%20movie%20content%2C%20warm%20ambient%20lighting%2C%20creative%20office%20atmosphere%2C%20coral%20and%20cream%20color%20palette%2C%20editorial%20photography%20style%2C%20natural%20light%20from%20windows%2C%20minimalist%20modern%20interior&width=1200&height=500&seq=about-team&orientation=landscape"
                alt="免费看片推荐团队"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">我们的承诺</h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                以用户为中心，坚持四个核心原则，为您提供最优质的免费看片平台推荐服务
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {highlights.map((item) => (
                <div key={item.title} className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">用户口碑</h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                听听用户们怎么说，真实反馈是我们最好的名片
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">联系我们</h2>
              <p className="text-sm md:text-base text-foreground-600">
                有任何问题或建议？欢迎给我们留言，我们会在24小时内回复
              </p>
            </div>
            <form
              onSubmit={handleContactSubmit}
              data-readdy-form
              className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8"
            >
              <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
              <div className="flex flex-col gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 text-sm bg-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    placeholder="请输入您的邮箱地址"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 text-sm bg-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                    <span className="text-foreground-400 font-normal ml-1">（最多500字）</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请输入您的留言内容..."
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 text-sm bg-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>
              </div>
              {contactStatus !== 'idle' && (
                <p className={`mt-4 text-sm text-center ${
                  contactStatus === 'success' ? 'text-primary-500' : 'text-red-500'
                }`}>
                  {contactMessage}
                </p>
              )}
            </form>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}